import { FacebookIcon, LinkedinIcon } from "@eworkit/iris";
import React, { memo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useMedia } from "react-use";

import { Anchor } from "@/components/Anchor";
import { Button } from "@/components/Button";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_MD } from "@/core/media";
import { Col, Flex, Row, Section } from "@/eworkitComponents";
import { useOnLanguageChange } from "@/hooks/Translations";
import { RouteButton } from "@/infrastructure/Route";
import { SessionContext } from "@/infrastructure/Session";
import { PublicAnchor } from "@/router/PublicAnchor";
import { Container } from "@/views/components/Container";
import { EworkLogo } from "@/views/components/Logo";
import { router } from "@/views/router";

import { LanguageSwitcher } from "../Topbar/LanguageSwitcher";

import "./_footer.scss";

const jobRequestsRoute = router.get("public-job-requests");
const dashboardRoute = router.get("dashboard");

export const Footer = memo(() => {
  const { t } = useTranslation("public");
  const lang = useOnLanguageChange();
  const isMedium = useMedia(MEDIA_SIZE_MD);
  const { authenticated } = useContext(SessionContext);
  const baseClass = "footer";

  return (
    <Section background="secondary" border={[true, false, false]} className={baseClass}>
      <Container>
        <Row>
          <Col width={{ xxs: 12, md: 2 }} className={`${baseClass}__logo__col`}>
            {authenticated ? (
              <Link to={dashboardRoute.path} className={`${baseClass}__logo`} target="_self" rel="noopener noreferrer">
                <EworkLogo size="xxl" orientation="landscape" />
              </Link>
            ) : (
              <Link to={`/${lang}${router.get("public-job-requests").path}`} target="_self">
                <EworkLogo size="xxl" orientation="landscape" />
              </Link>
            )}
          </Col>
          <Col width={{ xxs: 12, md: 9 }}>
            <Row>
              <div style={{ paddingTop: "2px" }}>
                <Flex
                  align="flex-start"
                  vertical-align="top"
                  direction={isMedium ? "column" : "row"}
                  innerMargin={isMedium ? "lg" : "xl"}
                  padding={[true]}>
                  <>
                    <RouteButton
                      path={`/${lang}${jobRequestsRoute.path}`}
                      variant="link"
                      size="lg"
                      display="inline-block"
                      testName="login"
                      className={`${baseClass}__link-button`}>
                      <Text size="lg">{t("LANDING_PAGE.BUTTON_FIND_JOBS")}</Text>
                    </RouteButton>
                    <PublicAnchor page="VMS" target="_blank" className={`route-button ${baseClass}__link-button`}>
                      <Button variant="link" size="lg" display="inline-block" className={`${baseClass}__link-button`}>
                        <Text size="lg">{t("VMS_PAGE.TITLE")}</Text>
                      </Button>
                    </PublicAnchor>

                    <PublicAnchor
                      page="CONSULTING-FIRM"
                      target="_blank"
                      className={`route-button ${baseClass}__link-button`}>
                      <Button variant="link" size="lg" display="inline-block" className={`${baseClass}__link-button`}>
                        <Text size="lg">{t("CONSULTING_PAGE.TITLE")}</Text>
                      </Button>
                    </PublicAnchor>
                  </>
                  <PublicAnchor target="_blank" page="ABOUT-US" className={`route-button ${baseClass}__link-button`}>
                    <Button variant="link" size="lg" display="inline-block" className={`${baseClass}__link-button`}>
                      <Text size="lg">{t("ABOUT_US_PAGE.TITLE")}</Text>
                    </Button>
                  </PublicAnchor>{" "}
                  <PublicAnchor target="_blank" page="BLOG" className={`route-button ${baseClass}__link-button`}>
                    <Button variant="link" size="lg" display="inline-block" className={`${baseClass}__link-button`}>
                      <Text size="lg">{t("FOOTER.BUTTON_BLOG")}</Text>
                    </Button>
                  </PublicAnchor>
                  <PublicAnchor target="_blank" page="CONTACT-US" className={`route-button ${baseClass}__link-button`}>
                    <Button variant="link" size="lg" display="inline-block" className={`${baseClass}__link-button`}>
                      <Text size="lg">{t("FOOTER.BUTTON_CONTACT_US")}</Text>
                    </Button>
                  </PublicAnchor>
                </Flex>
              </div>
            </Row>

            <Row direction={isMedium ? "column" : "row"}>
              <Flex justify="flex-start" direction="row" padding={[true]}>
                <Anchor href="https://www.facebook.com/eworkgroup" target="_blank">
                  <FacebookIcon size="lg" color="n700" className={`${baseClass}__icon`} />
                </Anchor>

                <Anchor href="https://www.linkedin.com/company/ework" target="_blank">
                  <LinkedinIcon size="lg" color="n700" className={`${baseClass}__icon`} />
                </Anchor>
              </Flex>
            </Row>
            <Row>
              <Flex
                align="flex-start"
                direction={isMedium ? "column" : "row"}
                innerMargin={isMedium ? "lg" : "xl"}
                padding={[true]}>
                <PublicAnchor
                  target="_blank"
                  page="USER-ACCESS-AGREEMENT"
                  className={`route-button ${baseClass}__link-button`}>
                  <Button variant="link" size="lg" display="inline-block" className={`${baseClass}__link-button`}>
                    <Text size="lg">{t("FOOTER.BUTTON_USER_ACCESS_AGREEMENT")}</Text>
                  </Button>
                </PublicAnchor>
                <PublicAnchor
                  target="_blank"
                  page="PRIVACY-POLICY"
                  className={`route-button ${baseClass}__link-button`}>
                  <Button variant="link" size="lg" display="inline-block" className={`${baseClass}__link-button`}>
                    <Text size="lg">{t("FOOTER.BUTTON_PRIVACY_POLICY")}</Text>
                  </Button>
                </PublicAnchor>
                <PublicAnchor target="_blank" page="COOKIE-POLICY" className={`route-button ${baseClass}__link-button`}>
                  <Button variant="link" size="lg" display="inline-block" className={`${baseClass}__link-button`}>
                    <Text size="lg">{t("FOOTER.BUTTON_COOKIE_POLICY")}</Text>
                  </Button>
                </PublicAnchor>
              </Flex>
            </Row>
            <Row>
              <Flex justify={isMedium ? "center" : "flex-end"} direction="row" padding={[true]}>
                {!authenticated && <LanguageSwitcher />}
              </Flex>
            </Row>
            <Row>
              <Flex
                justify="center"
                align="flex-start"
                direction="column"
                className={`${baseClass}__copyright`}
                padding={[true]}>
                <Text size="lg" color="n700">
                  {t("FOOTER.ALL_RIGHTS_RESERVED", { value: new Date().getFullYear() })}
                </Text>
                {isMedium && <br />}
                <Text size="lg" color="n700">
                  {t("FOOTER.DESCRIPTION")}
                </Text>
              </Flex>
            </Row>
          </Col>
        </Row>
      </Container>
    </Section>
  );
});
